import { useAvailableTenants } from '@/api/hooks/useAvailableTenants'
import { useSystemsList } from '@/api/hooks/useCrossroads'
import {
  Partner,
  type PartnerInstance,
  PartnerScope,
  useAllPartnerInstances,
  useAvailableScopes,
  useDeletePartner,
  usePartnerInstances,
  useTenantConfig,
  useUpsertPartner,
} from '@/api/hooks/useTenantConfig'
import { SystemType } from '@/api/types'
import { BasicSelectEditor } from '@/components/Grid/CellEditors/BasicSelectEditor'
import { HeaderWithTooltip } from '@/components/Grid/HeaderWithTooltip'
import { useTenantStore } from '@/stores/tenantStore'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { GraviGrid, Vertical } from '@gravitate-js/excalibrr'
import type {
  ColDef,
  ICellEditorParams,
  ICellRendererParams,
} from 'ag-grid-community'
import {
  Button,
  Checkbox,
  Drawer,
  Form,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tag,
  Typography,
  message,
  theme,
} from 'antd'
import type { CheckboxChangeEvent } from 'antd/es/checkbox'
import { forwardRef, useMemo, useState } from 'react'
import {
  ChangeManagementConfigModal,
  OrderChangeConfigInput,
} from './ChangeManagementConfigurationModal'
import { LearnMoreAlert } from './LearnMoreAlert'
import { ScopesModal } from './ScopesModal'
import { SourcingConfig, SourcingConfigModal } from './SourcingConfigModal'
import { connectedSystemType } from './connectedSystem'

const PartnerInstanceEditor = forwardRef(
  (props: ICellEditorParams & { partnerInstances: PartnerInstance[] }, ref) => {
    const options = props.partnerInstances.map((inst) => ({
      label: `${inst.name}${inst.system_name ? ` (${inst.system_name})` : ''}`,
      value: inst.id,
    }))

    return (
      <BasicSelectEditor
        {...props}
        ref={ref}
        options={options}
        value={props.data?.partner_instance_id || undefined}
        placeholder='Select instance'
      />
    )
  },
)

export const PartnersGrid = () => {
  const { token } = theme.useToken()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isScopesModalOpen, setIsScopesModalOpen] = useState(false)
  const [isSourcingConfigModalOpen, setIsSourcingConfigModalOpen] =
    useState(false)
  const [isChangeConfigModalOpen, setIsChangeConfigModalOpen] = useState(false)
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false)
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null)
  const [form] = Form.useForm()

  const currentTenantId = useTenantStore().selectedTenant?.id
  const { data: config, isLoading, refetch } = useTenantConfig()
  const { availableTenants } = useAvailableTenants()
  const { data: availableScopes } = useAvailableScopes()
  const { data: externalSystems } = useSystemsList()
  const upsertMutation = useUpsertPartner()
  const deleteMutation = useDeletePartner()

  const partners = config?.partners || []
  const instances = config?.instances || []

  const partnerTenantIds = useMemo(
    () => partners.map((p) => p.tenant_id),
    [partners],
  )
  const instancesByPartner = useAllPartnerInstances(partnerTenantIds)

  const selectedPartnerTenantId = Form.useWatch('partner_tenant_id', form)
  const { data: modalPartnerInstances, isLoading: isLoadingPartnerInstances } =
    usePartnerInstances(selectedPartnerTenantId)

  const columnDefs: ColDef[] = [
    {
      field: 'tenant_name',
      editable: false,
      headerName: 'Partner Tenant',
      flex: 1,
      minWidth: 180,
      headerComponent: HeaderWithTooltip,
      headerComponentParams: {
        tooltip: 'This is a tenant that is allowed to send you messages',
      },
      cellRenderer: (params: ICellRendererParams) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: 4,
              backgroundColor: token.colorFillSecondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
            }}
          >
            {params.value?.charAt(0)?.toUpperCase()}
          </div>
          {params.value}
        </div>
      ),
    },
    {
      field: 'partner_instance_id',
      headerName: 'Partner Instance',
      flex: 1,
      minWidth: 200,
      editable: true,
      headerComponent: HeaderWithTooltip,
      headerComponentParams: {
        tooltip:
          'The instance you want to accept partner messages from. Messages from your partner tenant that are from another instance will be blocked',
      },
      valueGetter: (params) =>
        params.data?.partner_instance_name || params.data?.partner_instance_id,
      cellEditor: PartnerInstanceEditor,
      cellEditorParams: (params: ICellEditorParams) => ({
        partnerInstances: instancesByPartner[params.data?.tenant_id] || [],
      }),
      suppressKeyboardEvent: (params) =>
        params.editing && params.event.key === 'Enter',
    },
    {
      field: 'instance_id',
      headerName: 'Connected Instance',
      width: 180,
      editable: true,
      headerComponent: HeaderWithTooltip,
      headerComponentParams: {
        tooltip:
          'The instance you want messages from your partner to be routed to',
      },
      valueGetter: (params) => {
        const instance = instances.find(
          (i) => i.id === params.data?.instance_id,
        )
        return instance?.name || params.data?.instance_id
      },
      cellEditor: BasicSelectEditor,
      cellEditorParams: {
        options: instances.map((instance) => ({
          label: instance.name || instance.system_id,
          value: instance.id,
        })),
      },
      suppressKeyboardEvent: (params) => {
        return params.editing && params.event.key === 'Enter'
      },
    },
    {
      field: 'dry_run',
      headerName: 'Dry Run',
      width: 100,
      headerComponent: HeaderWithTooltip,
      headerComponentParams: {
        tooltip:
          'When checked, enables "dry run" mode in your partner\'s Gravitate S&D. Crossroads will not block their S&D from taking operations when sending messages to you',
      },
      cellRenderer: (params: ICellRendererParams) => (
        <Checkbox
          checked={params.value}
          onChange={(e: CheckboxChangeEvent) => {
            const partner = params.data as Partner
            upsertMutation.mutate(
              {
                partner_tenant_id: partner.tenant_id,
                partner_instance_id: partner.partner_instance_id,
                instance_id: partner.instance_id,
                scopes: partner.scopes,
                dry_run: e.target.checked,
              },
              {
                onSuccess: () => {
                  message.success('Dry run setting updated')
                  refetch()
                },
                onError: (error) => {
                  message.error(`Failed to update dry run: ${error.message}`)
                },
              },
            )
          }}
        />
      ),
    },
    {
      field: 'scopes',
      headerName: 'Scopes',
      flex: 2,
      minWidth: 300,
      editable: false,
      headerComponent: HeaderWithTooltip,
      headerComponentParams: {
        tooltip:
          'The operations this partner is allowed to perform in your instance',
      },
      cellRenderer: (params: ICellRendererParams) => {
        const scopes = params.value || []
        const maxDisplay = 3
        const displayScopes = scopes.slice(0, maxDisplay)
        const remaining = scopes.length - maxDisplay

        return (
          <div
            style={{
              display: 'flex',
              gap: 4,
              flexWrap: 'wrap',
              alignItems: 'center',
              cursor: 'pointer',
            }}
            onClick={() => handleOpenScopesModal(params.data)}
          >
            {displayScopes.map((scope: string) => (
              <Tag key={scope}>{scope}</Tag>
            ))}
            {remaining > 0 && <Tag color='default'>+{remaining} more</Tag>}
            {scopes.length === 0 && (
              <span
                style={{ color: token.colorTextTertiary, fontStyle: 'italic' }}
              >
                Click to setup scopes
              </span>
            )}
          </div>
        )
      },
    },
    {
      field: 'sourcing_config',
      headerName: 'Sourcing Config',
      headerComponent: HeaderWithTooltip,
      headerComponentParams: {
        tooltip:
          'Controls how partner orders are supplied. When partners create orders in your software using Crossroads, these settings will be used to source the orders in your system',
      },
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <div
            style={{
              display: 'flex',
              gap: 4,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <Button
              size='small'
              type='default'
              onClick={() => {
                setIsSourcingConfigModalOpen(true)
                setSelectedPartner(params.data)
              }}
            >
              Configure
            </Button>
          </div>
        )
      },
    },
    {
      field: 'change_management_config',
      headerName: 'Order Change Config',
      headerComponent: HeaderWithTooltip,
      headerComponentParams: {
        tooltip:
          'Configures which order changes from this partner are accepted or require review',
      },
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <div
            style={{
              display: 'flex',
              gap: 4,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <Button
              size='small'
              type='default'
              onClick={() => {
                setIsChangeConfigModalOpen(true)
                setSelectedPartner(params.data)
              }}
            >
              Configure
            </Button>
          </div>
        )
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams) => (
        <Space size='small'>
          <Popconfirm
            title='Delete Partner'
            description={`Remove ${params.data.tenant_name} as a partner?`}
            onConfirm={() => handleDelete(params.data)}
            okText='Delete'
            okButtonProps={{ danger: true }}
          >
            <Button icon={<DeleteOutlined />} size='small' danger />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const handleOpenScopesModal = (partner: Partner) => {
    setSelectedPartner(partner)
    setIsScopesModalOpen(true)
  }

  const handleDelete = (partner: Partner) => {
    deleteMutation.mutate(
      {
        partnerTenantId: partner.tenant_id,
        partnerInstanceId: partner.partner_instance_id,
      },
      {
        onSuccess: () => {
          message.success('Partner removed successfully')
          refetch()
        },
        onError: (error) => {
          message.error(`Failed to remove partner: ${error.message}`)
        },
      },
    )
  }

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      // Preserve existing scopes when editing, default to empty for new partners
      const scopes = selectedPartner?.scopes || []
      upsertMutation.mutate(
        { ...values, scopes },
        {
          onSuccess: () => {
            message.success(
              selectedPartner
                ? 'Partner updated successfully'
                : 'Partner added successfully',
            )
            setIsModalOpen(false)
            setSelectedPartner(null)
            form.resetFields()
            refetch()
          },
          onError: (error) => {
            message.error(`Failed to save partner: ${error.message}`)
          },
        },
      )
    })
  }

  const handleScopesUpdate = (scopes: PartnerScope[]) => {
    if (!selectedPartner) return

    upsertMutation.mutate(
      {
        partner_tenant_id: selectedPartner.tenant_id,
        partner_instance_id: selectedPartner.partner_instance_id,
        instance_id: selectedPartner.instance_id,
        scopes,
      },
      {
        onSuccess: () => {
          message.success('Scopes updated successfully')
          setIsScopesModalOpen(false)
          setSelectedPartner(null)
          refetch()
        },
        onError: (error) => {
          message.error(`Failed to update scopes: ${error.message}`)
        },
      },
    )
  }

  const handleSourcingConfigUpdate = (config: SourcingConfig) => {
    if (!selectedPartner) return

    upsertMutation.mutate(
      {
        partner_tenant_id: selectedPartner.tenant_id,
        partner_instance_id: selectedPartner.partner_instance_id,
        instance_id: selectedPartner.instance_id,
        scopes: selectedPartner.scopes,
        sourcing_config: config,
      },
      {
        onSuccess: () => {
          message.success('Sourcing configuration updated successfully')
          setIsSourcingConfigModalOpen(false)
          setSelectedPartner(null)
          refetch()
        },
        onError: (error) => {
          message.error(
            `Failed to update sourcing configuration: ${error.message}`,
          )
        },
      },
    )
  }

  const handleChangeConfigUpdate = (config: OrderChangeConfigInput) => {
    if (!selectedPartner) return

    upsertMutation.mutate(
      {
        partner_tenant_id: selectedPartner.tenant_id,
        partner_instance_id: selectedPartner.partner_instance_id,
        instance_id: selectedPartner.instance_id,
        scopes: selectedPartner.scopes,
        change_management_config: config,
      },
      {
        onSuccess: () => {
          message.success('Change configuration updated successfully')
          setIsChangeConfigModalOpen(false)
          setSelectedPartner(null)
          refetch()
        },
        onError: (error) => {
          message.error(
            `Failed to update change configuration: ${error.message}`,
          )
        },
      },
    )
  }

  // The connected (local) instance the selected partner routes to. When its system is an
  // external (non-S&D) system, order changes can never be auto-accepted (enforced
  // server-side), so we lock the auto-accept handles in the change-config modal.
  const isConnectedInstanceExternal = useMemo(() => {
    if (!selectedPartner || !externalSystems) return false
    return (
      connectedSystemType(
        selectedPartner.instance_id,
        instances,
        externalSystems,
      ) === SystemType.EXTERNAL
    )
  }, [selectedPartner, instances, externalSystems])

  // Allow all tenants except self
  const availablePartnerTenants = availableTenants.filter(
    (t) => t.id !== currentTenantId,
  )

  // Filter out partner instances already connected for the selected tenant
  const usedInstanceIds = useMemo(
    () =>
      partners
        .filter((p) => p.tenant_id === selectedPartnerTenantId)
        .map((p) => p.partner_instance_id),
    [partners, selectedPartnerTenantId],
  )
  const availableModalPartnerInstances = useMemo(
    () =>
      (modalPartnerInstances || []).filter(
        (inst) => !usedInstanceIds.includes(inst.id),
      ),
    [modalPartnerInstances, usedInstanceIds],
  )

  return (
    <Vertical bg='layout' style={{ height: '100%', width: '100%' }}>
      <LearnMoreAlert
        message="Configure which partners can send you messages and how they're handled."
        onLearnMore={() => setIsLearnMoreOpen(true)}
      />
      <GraviGrid
        updateEP={async (values) => {
          const { tenant_id, tenant_name, ...rest } = values
          upsertMutation.mutate(
            {
              partner_tenant_id: tenant_id,
              ...rest,
            },
            {
              onSuccess: () => {
                message.success('Partner updated successfully')
                refetch()
              },
              onError: (error) => {
                message.error(`Failed to save partner: ${error.message}`)
              },
            },
          )
        }}
        rowData={partners}
        columnDefs={columnDefs}
        loading={isLoading}
        controlBarProps={{
          title: 'Partner Access',
          actionButtons: (
            <Button
              type='primary'
              icon={<PlusOutlined />}
              onClick={() => {
                setSelectedPartner(null)
                form.resetFields()
                setIsModalOpen(true)
              }}
            >
              Add Partner
            </Button>
          ),
        }}
        agPropOverrides={{
          context: { tenantConfig: config },
          getRowId: (params) =>
            `${params.data.tenant_id}_${params.data.partner_instance_id}`,
          defaultColDef: {
            sortable: true,
            filter: true,
            resizable: true,
          },
          animateRows: true,
        }}
      />

      <Modal
        title={selectedPartner ? 'Edit Partner' : 'Add Partner'}
        open={isModalOpen}
        onOk={handleModalOk}
        onCancel={() => {
          setIsModalOpen(false)
          setSelectedPartner(null)
          form.resetFields()
        }}
        confirmLoading={upsertMutation.isPending}
      >
        <Form form={form} layout='vertical'>
          <Form.Item
            name='partner_tenant_id'
            label='Partner Tenant'
            rules={[
              { required: true, message: 'Please select a partner tenant' },
            ]}
          >
            <Select
              placeholder='Select a tenant'
              disabled={!!selectedPartner}
              showSearch
              optionFilterProp='children'
              onChange={() =>
                form.setFieldValue('partner_instance_id', undefined)
              }
            >
              {availablePartnerTenants.map((tenant) => (
                <Select.Option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name='partner_instance_id'
            label='Partner Instance'
            rules={[
              { required: true, message: 'Please select a partner instance' },
            ]}
          >
            <Select
              placeholder='Select a partner instance'
              disabled={!selectedPartnerTenantId}
              loading={isLoadingPartnerInstances}
              showSearch
              optionFilterProp='label'
              options={availableModalPartnerInstances.map((inst) => ({
                label: `${inst.name}${inst.system_name ? ` (${inst.system_name})` : ''}`,
                value: inst.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            name='instance_id'
            label='Connected Instance'
            rules={[{ required: true, message: 'Please select an instance' }]}
          >
            <Select placeholder='Select an instance'>
              {instances.map((instance) => (
                <Select.Option key={instance.id} value={instance.id}>
                  {instance.name || instance.system_id}{' '}
                  {instance.primary && '(Primary)'}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <ScopesModal
        open={isScopesModalOpen}
        onClose={() => {
          setIsScopesModalOpen(false)
          setSelectedPartner(null)
        }}
        partner={selectedPartner}
        availableScopes={availableScopes || []}
        onSave={handleScopesUpdate}
        loading={upsertMutation.isPending}
      />

      <SourcingConfigModal
        open={isSourcingConfigModalOpen}
        onClose={() => {
          setIsSourcingConfigModalOpen(false)
          setSelectedPartner(null)
        }}
        partner={selectedPartner}
        onSave={handleSourcingConfigUpdate}
        loading={upsertMutation.isPending}
      />

      <ChangeManagementConfigModal
        open={isChangeConfigModalOpen}
        onClose={() => {
          setIsChangeConfigModalOpen(false)
          setSelectedPartner(null)
        }}
        partner={selectedPartner}
        onSave={handleChangeConfigUpdate}
        loading={upsertMutation.isPending}
        autoAcceptDisabled={isConnectedInstanceExternal}
      />

      <Drawer
        title='About Partners'
        open={isLearnMoreOpen}
        onClose={() => setIsLearnMoreOpen(false)}
      >
        <Typography.Paragraph>
          Partners are the other tenants that you are integrated with. On this
          screen you can configure which tenants are allowed to send you
          messages and how you want to handle them.
        </Typography.Paragraph>
        <br />
        <Typography.Paragraph>
          To configure how the messages you send are handled, work with the
          partner you are sending the messages to and have them update their
          partners list.
        </Typography.Paragraph>
      </Drawer>
    </Vertical>
  )
}
